import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  Alert,
  Divider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  InstallDesktop as InstallIcon,
  Login as LoginIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Help as HelpIcon,
} from '@mui/icons-material';

interface DesktopAppSetupInstructionsProps {
  platform?: 'windows' | 'macos';
}

const DesktopAppSetupInstructions: React.FC<DesktopAppSetupInstructionsProps> = ({ 
  platform = 'windows' 
}) => {
  const steps = platform === 'windows' ? [
    {
      label: 'Download the Installer',
      icon: <DownloadIcon />,
      content: (
        <Box>
          <Typography variant="body2" paragraph>
            Click the download button above to download the Windows installer (.msi file).
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The installer will be saved to your Downloads folder.
          </Typography>
        </Box>
      ),
    },
    {
      label: 'Run the Installer',
      icon: <InstallIcon />,
      content: (
        <Box>
          <Typography variant="body2" paragraph>
            Locate the downloaded .msi file and double-click to run the installer.
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Follow the installation wizard"
                secondary="Accept the license agreement and choose installation location"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Complete the installation"
                secondary="The installer will set up the desktop app on your system"
              />
            </ListItem>
          </List>
        </Box>
      ),
    },
    {
      label: 'Launch the Desktop App',
      icon: <LoginIcon />,
      content: (
        <Box>
          <Typography variant="body2" paragraph>
            After installation, launch the Branch Router desktop app from the Start menu or desktop shortcut.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            The app will start minimized in the system tray. Look for the Branch Router icon in your taskbar.
          </Alert>
        </Box>
      ),
    },
    {
      label: 'Log In',
      icon: <LoginIcon />,
      content: (
        <Box>
          <Typography variant="body2" paragraph>
            Use your account credentials to log in to the desktop app.
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Enter your email and password"
                secondary="Use the same credentials you use for the web app"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Your credentials are securely stored"
                secondary="Windows Credential Manager will store your authentication token"
              />
            </ListItem>
          </List>
        </Box>
      ),
    },
    {
      label: 'Configure Alerts',
      icon: <SettingsIcon />,
      content: (
        <Box>
          <Typography variant="body2" paragraph>
            Set up your alert configurations in the desktop app.
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Create alert configurations"
                secondary="Define ticker symbols, position types, and webhook URLs"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Configure secret words"
                secondary="Set up secret words to match your signal source alerts"
              />
            </ListItem>
          </List>
        </Box>
      ),
    },
    {
      label: 'Start Capturing Toast Notifications',
      icon: <NotificationsIcon />,
      content: (
        <Box>
          <Typography variant="body2" paragraph>
            Once configured, the desktop app will automatically capture toast notifications from your signal source platform.
          </Typography>
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              You're all set! The desktop app will now monitor for signal source alerts and execute your webhooks automatically.
            </Typography>
          </Alert>
        </Box>
      ),
    },
  ] : [
    // macOS steps would go here (similar structure)
    {
      label: 'Download for macOS',
      icon: <DownloadIcon />,
      content: <Typography variant="body2">macOS installation instructions coming soon.</Typography>,
    },
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Setup Instructions
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Follow these steps to set up the desktop app on your {platform === 'windows' ? 'Windows' : 'macOS'} computer.
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Stepper orientation="vertical">
            {steps.map((step, index) => (
              <Step key={index} active={true} completed={false}>
                <StepLabel StepIconComponent={() => <Box sx={{ color: 'primary.main' }}>{step.icon}</Box>}>
                  {step.label}
                </StepLabel>
                <StepContent>
                  {step.content}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="h6" gutterBottom>
            <HelpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Troubleshooting Tips
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Ensure Windows 10/11 is up to date"
                secondary="The desktop app requires the latest Windows updates for toast notification capture"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Check Windows Credential Manager"
                secondary="If login fails, check Windows Credential Manager for stored tokens"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Verify your signal source app is running"
                secondary="The desktop app can only capture toast notifications when your signal source app is active"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Check firewall settings"
                secondary="Ensure firewall allows webhook execution from the desktop app"
              />
            </ListItem>
          </List>
        </Box>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Need help?</strong> Visit our{' '}
            <a href="https://docs.tos-autotrade.com" target="_blank" rel="noopener noreferrer">
              documentation
            </a>{' '}
            or contact support for assistance.
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default DesktopAppSetupInstructions;

